import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import useAuthStore from './store/authStore';

import AppLayout from './components/layout/AppLayout';
import ProtectedRoute from './components/layout/ProtectedRoute';
import HomePage from './pages/HomePage';
import NotFoundPage from './pages/NotFoundPage';

/* 첫 화면에 필요 없는 페이지는 지연 로딩. 홈 번들에서 jsPDF · DXF 파서 · three.js 를 뺀다. */
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const SignupPage = lazy(() => import('./pages/auth/SignupPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const QuotePage = lazy(() => import('./pages/quote/QuotePage'));
const DxfViewerPage = lazy(() => import('./pages/viewer/DxfViewerPage'));
const MaterialsPage = lazy(() => import('./pages/MaterialsPage'));
const GalleryPage = lazy(() => import('./pages/gallery/GalleryPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const AboutPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.AboutPage })));
const TermsPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.PrivacyPage })));

const MyPageLayout = lazy(() => import('./pages/mypage/MyPageLayout'));
const DashboardPage = lazy(() => import('./pages/mypage/DashboardPage'));
const OrdersPage = lazy(() => import('./pages/mypage/OrdersPage'));
const OrderDetailPage = lazy(() => import('./pages/mypage/OrderDetailPage'));
const QuotesPage = lazy(() => import('./pages/mypage/QuotesPage').then(m => ({ default: m.QuotesPage })));
const QuoteDetailPage = lazy(() => import('./pages/mypage/QuotesPage').then(m => ({ default: m.QuoteDetailPage })));
const NestingPage = lazy(() => import('./pages/mypage/NestingPage'));
const CertificatesPage = lazy(() => import('./pages/mypage/CertificatesPage'));
const ProfilePage = lazy(() => import('./pages/mypage/ProfilePage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 15000
    }
  }
});

function Boot({ children }) {
  const init = useAuthStore(s => s.init);
  useEffect(() => {
    init();
  }, [init]);
  return children;
}

function PageFallback() {
  return (
    <div className="wrap" style={{ padding: '48px 0' }} aria-busy="true">
      <div className="skel" style={{ height: 14, width: 120, marginBottom: 14 }} />
      <div className="skel" style={{ height: 34, width: 320, marginBottom: 24 }} />
      <div className="skel" style={{ height: 240 }} />
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Boot>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route element={<AppLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />

                <Route path="/quote" element={<QuotePage />} />
                <Route path="/list" element={<Navigate to="/quote" replace />} />
                <Route path="/materials" element={<MaterialsPage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />

                <Route path="/viewer" element={<DxfViewerPage />} />
                {/* 구 링크 호환. QA 문서는 마이페이지 증명서로 통합됐다. */}
                <Route path="/qa-documents" element={<Navigate to="/mypage/certificates" replace />} />

                <Route element={<ProtectedRoute />}>
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/mypage" element={<MyPageLayout />}>
                    <Route index element={<DashboardPage />} />
                    <Route path="orders" element={<OrdersPage />} />
                    <Route path="orders/:orderNumber" element={<OrderDetailPage />} />
                    <Route path="quotes" element={<QuotesPage />} />
                    <Route path="quotes/:quoteNumber" element={<QuoteDetailPage />} />
                    <Route path="nesting" element={<NestingPage />} />
                    <Route path="certificates" element={<CertificatesPage />} />
                    <Route path="profile" element={<ProfilePage />} />
                  </Route>
                </Route>

                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </Suspense>
        </Boot>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
